import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { PanelLeft } from "lucide-react"

// import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@utils/clsx"
import { Button } from "@component/UI/button"
import { Input } from "@component/UI/input"
import { Separator } from "@component/UI/separator"
import { Sheet, SheetContent } from "@component/UI/sheet"
import { Skeleton } from "@component/UI/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@component/UI/tooltip"

const SIDEBAR_COOKIE_NAME = "sidebar:state"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = "16rem"
const SIDEBAR_WIDTH_MOBILE = "18rem"
const SIDEBAR_WIDTH_ICON = "3rem"
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

/**
 * @typedef {"expanded" | "collapsed"} SidebarState
 */

/**
 * @typedef {Object} SidebarContextType
 * @property {SidebarState} state - The current sidebar state.
 * @property {boolean} open - Whether the sidebar is open on desktop.
 * @property {(open: boolean) => void} setOpen - Sets the desktop sidebar open state.
 * @property {boolean} openMobile - Whether the sidebar is open on mobile.
 * @property {(open: boolean) => void} setOpenMobile - Sets the mobile sidebar open state.
 * @property {boolean} isMobile - Indicates if the current screen is mobile-sized.
 * @property {() => void} toggleSidebar - Toggles the sidebar open/collapsed state.
 */

/** @type {React.Context<SidebarContextType | null>} */
const SidebarContext = React.createContext(null);

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }

  return context
}

/**
 * @typedef {Object} SidebarProviderProps
 * @property {boolean} [defaultOpen=true] - Whether the sidebar is open by default.
 * @property {boolean} [open] - Controlled open state.
 * @property {(open: boolean) => void} [onOpenChange] - Callback fired when open state changes.
 * @property {React.ReactNode} [children] - Child components.
 * @property {string} [className] - Optional class name for styling.
 * @property {React.CSSProperties} [style] - Inline styles.
 * @property {ref}
 * @property {...React.HTMLAttributes<HTMLDivElement>} [props] - Additional div props.
 */

/**
 * Sidebar provider component that manages sidebar open state and context.
 * 
 * @type {React.ForwardRefExoticComponent<
 *   SidebarProviderProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const SidebarProvider = React.forwardRef(function SidebarProvider(
  {
    defaultOpen = true,
    open: openProp,
    onOpenChange: setOpenProp,
    className,
    style,
    children,
    mainRef,
    ...props
  },
  ref
) {
    // const isMobile = useIsMobile()
    const [openMobile, setOpenMobile] = React.useState(false)

    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(defaultOpen)
    const open = openProp ?? _open
    const setOpen = React.useCallback(
      (value) => {
        const openState = typeof value === "function" ? value(open) : value
        if (setOpenProp) {
          setOpenProp(openState)
        } else {
          _setOpen(openState)
        }

        // This sets the cookie to keep the sidebar state.
        document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
      },
      [setOpenProp, open]
    )

    // Helper to toggle the sidebar.
    const toggleSidebar = React.useCallback(() => {
      return setOpen((open) => !open)
    }, [setOpen, setOpenMobile])

    // Adds a keyboard shortcut to toggle the sidebar.
    // React.useEffect(() => {
    //   const handleKeyDown = (event) => {
    //     if (
    //       event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
    //       (event.metaKey || event.ctrlKey)
    //     ) {
    //       event.preventDefault()
    //       toggleSidebar()
    //     }
    //   }

    //   window.addEventListener("keydown", handleKeyDown)
    //   return () => window.removeEventListener("keydown", handleKeyDown)
    // }, [toggleSidebar])

    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const state = open ? "expanded" : "collapsed"

    const contextValue = React.useMemo(
      () => ({
        state,
        open,
        setOpen,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        mainRef,
      }),
      [state, open, setOpen, openMobile, setOpenMobile, toggleSidebar, mainRef]
    )

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            style={
              {
                "--sidebar-width": SIDEBAR_WIDTH,
                "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
                ...style,
              }
            }
            className={cn(
              "group/sidebar-wrapper flex min-h-svh w-full has-[[data-variant=inset]]:bg-sidebar",
              className
            )}
            ref={ref}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    )
  }
)
SidebarProvider.displayName = "SidebarProvider"

/**
 * @typedef {"left" | "right"} SidebarSide
 * @typedef {"sidebar" | "floating" | "inset"} SidebarVariant
 * @typedef {"offcanvas" | "icon" | "none"} SidebarCollapsible
 */

/**
 * @typedef {Object} SidebarProps
 * @property {SidebarSide} [side="left"] - Side of the screen the sidebar appears on.
 * @property {SidebarVariant} [variant="sidebar"] - Visual style variant of the sidebar.
 * @property {SidebarCollapsible} [collapsible="offcanvas"] - Collapsing behavior of the sidebar.
 * @property {string} [className] - Optional class name for styling.
 * @property {React.ReactNode} [children] - Content of the sidebar.
 * @property {...React.HTMLAttributes<HTMLDivElement>} [props] - Additional div props.
 */

/**
 * Sidebar component with customizable position, style, and collapse behavior.
 * 
 * @type {React.ForwardRefExoticComponent<
 *   SidebarProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const Sidebar = React.forwardRef(function Sidebar(
  {
    side = "left",
    variant = "sidebar",
    collapsible = "offcanvas",
    className,
    children,
    ...props
  },
  ref
) {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar()

    if (collapsible === "none") {
      return (
        <div
          className={cn(
            "flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground",
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      )
    }

    if (isMobile) {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
          <SheetContent
            data-sidebar="sidebar"
            data-mobile="true"
            className="w-[--sidebar-width] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
            style={
              {
                "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
              }
            }
            side={side}
          >
            <div className="flex h-full w-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      )
    }

    return (
      <div
        ref={ref}
        className="group peer hidden md:block text-sidebar-foreground"
        data-state={state}
        data-collapsible={state === "collapsed" ? collapsible : ""}
        data-variant={variant}
        data-side={side}
      >
        {/* This is what handles the sidebar gap on desktop */}
        <div
          className={cn(
            "duration-200 relative h-svh w-[--sidebar-width] bg-transparent transition-[width] ease-linear",
            "group-data-[collapsible=offcanvas]:w-0",
            "group-data-[side=right]:rotate-180",
            variant === "floating" || variant === "inset"
              ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]"
              : "group-data-[collapsible=icon]:w-[--sidebar-width-icon]"
          )}
        />
        <div
          className={cn(
            "duration-200 fixed inset-y-0 z-10 hidden h-svh w-[--sidebar-width] transition-[left,right,width] ease-linear md:flex",
            side === "left"
              ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
              : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
            // Adjust the padding for floating and inset variants.
            variant === "floating" || variant === "inset"
              ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]"
              : "group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l",
            className
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow"
          >
            {children}
          </div>
        </div>
      </div>
    )
  }
)
Sidebar.displayName = "Sidebar"

/**
 * @typedef {React.ComponentProps<typeof Button>} SidebarTriggerProps
 */

/**
 * Sidebar trigger button component that toggles the sidebar.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarTriggerProps & React.RefAttributes<React.ElementRef<typeof Button>>
 * >}
 */
const SidebarTrigger = React.forwardRef(function SidebarTrigger(
  { className, onClick, ...props },
  ref
) {
  const { toggleSidebar } = useSidebar()

  return (
    <Button
      ref={ref}
      data-sidebar="trigger"
      variant="ghost"
      size="icon"
      className={cn("h-7 w-7", className)}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <PanelLeft />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
})
SidebarTrigger.displayName = "SidebarTrigger"

/**
 * @typedef {React.ComponentProps<"button">} SidebarRailProps
 */

/**
 * SidebarRail component used as a minimal button (rail) to control sidebar behavior.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarRailProps & React.RefAttributes<HTMLButtonElement>
 * >}
 */
const SidebarRail = React.forwardRef(function SidebarRail(
  { className, ...props },
  ref
) {
  const { toggleSidebar } = useSidebar()

  return (
    <button
      ref={ref}
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
        "[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-sidebar",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className
      )}
      {...props}
    />
  )
})
SidebarRail.displayName = "SidebarRail"

/**
 * @typedef {React.ComponentProps<"main">} SidebarInsetProps
 */

/**
 * SidebarInset is a layout wrapper typically used to contain the main content area,
 * allowing room for the sidebar while maintaining semantic structure.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarInsetProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const SidebarInset = React.forwardRef(function SidebarInset(
  { className, ...props },
  ref
) {
  return (
    <main
      ref={ref}
      className={cn(
        "relative flex min-h-svh flex-1 flex-col bg-background",
        "peer-data-[variant=inset]:min-h-[calc(100svh-theme(spacing.4))] md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow",
        className
      )}
      {...props}
    />
  )
})
SidebarInset.displayName = "SidebarInset"

/**
 * @typedef {React.ComponentProps<typeof Input>} SidebarInputProps
 */

/**
 * SidebarInput is a styled input component used within the sidebar,
 * typically wrapping a base Input component.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarInputProps & React.RefAttributes<React.ElementRef<typeof Input>>
 * >}
 */
const SidebarInput = React.forwardRef(function SidebarInput(
  { className, ...props },
  ref
) {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        "h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        className
      )}
      {...props}
    />
  )
})
SidebarInput.displayName = "SidebarInput"

/**
 * @typedef {React.ComponentProps<"div">} SidebarHeaderProps
 */

/**
 * SidebarHeader is a container component used to display header content
 * (such as title, actions, or branding) at the top of the sidebar.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarHeaderProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const SidebarHeader = React.forwardRef(function SidebarHeader(
  { className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
})
SidebarHeader.displayName = "SidebarHeader"

/**
 * @typedef {React.ComponentProps<"div">} SidebarFooterProps
 */

/**
 * SidebarFooter is a container component used to render content
 * (such as actions, links, or status info) at the bottom of the sidebar.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarFooterProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const SidebarFooter = React.forwardRef(function SidebarFooter(
  { className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
})
SidebarFooter.displayName = "SidebarFooter"

/**
 * @typedef {React.ComponentProps<typeof Separator>} SidebarSeparatorProps
 */

/**
 * SidebarSeparator is a visual divider component used to separate sections
 * within the sidebar, typically wrapping a base Separator component.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarSeparatorProps & React.RefAttributes<React.ElementRef<typeof Separator>>
 * >}
 */
const SidebarSeparator = React.forwardRef(function SidebarSeparator(
  { className, ...props },
  ref
) {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn("mx-2 w-auto bg-sidebar-border", className)}
      {...props}
    />
  )
})
SidebarSeparator.displayName = "SidebarSeparator"

/**
 * @typedef {React.ComponentProps<"div">} SidebarContentProps
 */

/**
 * SidebarContent is the main scrollable content area inside the sidebar,
 * typically used to house navigation, lists, or interactive elements.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarContentProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const SidebarContent = React.forwardRef(function SidebarContent(
  { className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      {...props}
    />
  )
})
SidebarContent.displayName = "SidebarContent"

/**
 * @typedef {React.ComponentProps<"div">} SidebarGroupProps
 */

/**
 * SidebarGroup is a layout component used to group related sidebar items together.
 * Commonly used for organizing navigation sections, filters, or categorized content.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarGroupProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const SidebarGroup = React.forwardRef(function SidebarGroup(
  { className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      {...props}
    />
  )
})
SidebarGroup.displayName = "SidebarGroup"

/**
 * @typedef {React.ComponentProps<"div"> & { asChild?: boolean }} SidebarGroupLabelProps
 */

/**
 * SidebarGroupLabel is used to label a group of sidebar items.
 * The `asChild` prop allows rendering a custom component instead of a `div`.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarGroupLabelProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const SidebarGroupLabel = React.forwardRef(function SidebarGroupLabel(
  { className, asChild = false, ...props },
  ref
) {
  const Comp = asChild ? Slot : "div"

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        "duration-200 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opa] ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className
      )}
      {...props}
    />
  )
})
SidebarGroupLabel.displayName = "SidebarGroupLabel"

/**
 * @typedef {React.ComponentProps<"button"> & { asChild?: boolean }} SidebarGroupActionProps
 */

/**
 * SidebarGroupAction is an interactive element used within a sidebar group
 * (e.g. for triggering settings, filters, or collapsible actions).
 * The `asChild` prop allows rendering a custom component instead of a native button.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarGroupActionProps & React.RefAttributes<HTMLButtonElement>
 * >}
 */
const SidebarGroupAction = React.forwardRef(function SidebarGroupAction(
  { className, asChild = false, ...props },
  ref
) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        "absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform  focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
})
SidebarGroupAction.displayName = "SidebarGroupAction"

/**
 * @typedef {React.ComponentProps<"div">} SidebarGroupContentProps
 */

/**
 * SidebarGroupContent is a container for rendering the children of a sidebar group,
 * such as nested navigation items or grouped elements.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarGroupContentProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const SidebarGroupContent = React.forwardRef(function SidebarGroupContent(
  { className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      data-sidebar="group-content"
      className={cn("w-full text-sm", className)}
      {...props}
    />
  );
});
SidebarGroupContent.displayName = "SidebarGroupContent"

/**
 * @typedef {React.ComponentProps<"ul">} SidebarMenuProps
 */

/**
 * SidebarMenu is a list container typically used to render grouped navigation
 * or menu items within the sidebar.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarMenuProps & React.RefAttributes<HTMLUListElement>
 * >}
 */
const SidebarMenu = React.forwardRef(function SidebarMenu(
  { className, ...props },
  ref
) {
  return (<ul
    ref={ref}
    data-sidebar="menu"
    className={cn("flex w-full min-w-0 flex-col gap-1", className)}
    {...props}
  />)
})
SidebarMenu.displayName = "SidebarMenu"

/**
 * @typedef {React.ComponentProps<"li">} SidebarMenuItemProps
 */

/**
 * SidebarMenuItem is a list item used within a SidebarMenu to represent
 * an individual navigable or interactive element in the sidebar.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarMenuItemProps & React.RefAttributes<HTMLLIElement>
 * >}
 */
const SidebarMenuItem = React.forwardRef(function SidebarMenuItem(
  { className, ...props },
  ref
) {
  return (<li
    ref={ref}
    data-sidebar="menu-item"
    className={cn("group/menu-item relative", className)}
    {...props}
  />)
})
SidebarMenuItem.displayName = "SidebarMenuItem"

const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding]  focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "",
        outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:!p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * @typedef {React.ComponentProps<"button">} BaseButtonProps
 * @typedef {import("react").ComponentProps<typeof TooltipContent>} TooltipContentProps
 * @typedef {import("class-variance-authority").VariantProps<typeof sidebarMenuButtonVariants>} SidebarMenuButtonVariantProps
 */

/**
 * @typedef {Object} SidebarMenuButtonExtraProps
 * @property {boolean} [asChild=false] - Render as Slot child.
 * @property {boolean} [isActive=false] - Active state.
 * @property {string | TooltipContentProps} [tooltip] - Tooltip content.
 */

/**
 * @typedef {BaseButtonProps & SidebarMenuButtonVariantProps & SidebarMenuButtonExtraProps} SidebarMenuButtonProps
 */

/**
 * SidebarMenuButton is an interactive sidebar control with support for:
 * - `asChild` slot rendering
 * - `isActive` state styling
 * - tooltip (text or config object)
 * - `variant` and `size` props from `sidebarMenuButtonVariants` (CVA)
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarMenuButtonProps & React.RefAttributes<HTMLButtonElement>
 * >}
 */
const SidebarMenuButton = React.forwardRef(function SidebarMenuButton(
  {
    asChild = false,
    isActive = false,
    variant = "default",
    size = "default",
    tooltip,
    className,
    ...props
  },
  ref
) {
    const Comp = asChild ? Slot : "button"
    const { isMobile, state } = useSidebar()

    const button = (
      <Comp
        ref={ref}
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
        {...props}
      />
    )

    if (!tooltip) {
      return button
    }

    if (typeof tooltip === "string") {
      tooltip = {
        children: tooltip,
      }
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={state !== "collapsed" || isMobile}
          {...tooltip}
        />
      </Tooltip>
    )
  }
)
SidebarMenuButton.displayName = "SidebarMenuButton"

/**
 * @typedef {React.ComponentProps<"button">} BaseButtonProps
 */

/**
 * @typedef {BaseButtonProps & {
 *   /**
 *    * Render using a Slot wrapper (e.g., Radix UI's `asChild` pattern).
 *    * @default false
 *    *\/
 *   asChild?: boolean,

 *   /**
 *    * Determines whether the button only becomes visible on hover.
 *    * @default false
 *    *\/
 *   showOnHover?: boolean
 * }} SidebarMenuActionProps
 */

/**
 * SidebarMenuAction is a button typically used for quick actions (like edit/delete)
 * inside sidebar menu items. It supports:
 * - `asChild` for flexible rendering
 * - `showOnHover` to hide the button until hover state is triggered
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarMenuActionProps & React.RefAttributes<HTMLButtonElement>
 * >}
 */
const SidebarMenuAction = React.forwardRef(function SidebarMenuAction(
  { className, asChild = false, showOnHover = false, ...props },
  ref
) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-action"
      className={cn(
        "absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0",
        className
      )}
      {...props}
    />
  )
})
SidebarMenuAction.displayName = "SidebarMenuAction"

/**
 * @typedef {React.ComponentProps<"div">} SidebarMenuBadgeProps
 */

/**
 * SidebarMenuBadge is a visual indicator typically used to display
 * counts, status, or flags (e.g., unread messages) alongside sidebar menu items.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarMenuBadgeProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const SidebarMenuBadge = React.forwardRef(function SidebarMenuBadge(
  { className, ...props },
  ref
) {
  return (<div
    ref={ref}
    data-sidebar="menu-badge"
    className={cn(
      "absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none",
      "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
      "peer-data-[size=sm]/menu-button:top-1",
      "peer-data-[size=default]/menu-button:top-1.5",
      "peer-data-[size=lg]/menu-button:top-2.5",
      "group-data-[collapsible=icon]:hidden",
      className
    )}
    {...props}
  />
)})
SidebarMenuBadge.displayName = "SidebarMenuBadge"

/**
 * @typedef {React.ComponentProps<"div"> & {
 *   /**
 *    * Whether to show an icon placeholder in the skeleton.
 *    * @default false
 *    *\/
 *   showIcon?: boolean
 * }} SidebarMenuSkeletonProps
 */

/**
 * SidebarMenuSkeleton is a placeholder component used during loading states
 * to represent the shape of a sidebar menu item. It optionally displays
 * an icon skeleton alongside the text line.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarMenuSkeletonProps & React.RefAttributes<HTMLDivElement>
 * >}
 */
const SidebarMenuSkeleton = React.forwardRef(function SidebarMenuSkeleton(
  { className, showIcon = false, ...props },
  ref
) {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`
  }, [])

  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={cn("rounded-md h-8 flex gap-2 px-2 items-center", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 flex-1 max-w-[--skeleton-width]"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          }
        }
      />
    </div>
  )
})
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton"

/**
 * @typedef {React.ComponentProps<"ul">} SidebarMenuSubProps
 */

/**
 * SidebarMenuSub is a nested `<ul>` element used to render sub-items
 * within a `SidebarMenuItem`, providing support for multi-level navigation
 * inside the sidebar.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarMenuSubProps & React.RefAttributes<HTMLUListElement>
 * >}
 */
const SidebarMenuSub = React.forwardRef(function SidebarMenuSub(
  { className, ...props },
  ref
) {
  <ul
    ref={ref}
    data-sidebar="menu-sub"
    className={cn(
      "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5",
      "group-data-[collapsible=icon]:hidden",
      className
    )}
    {...props}
  />
 })
SidebarMenuSub.displayName = "SidebarMenuSub"

/**
 * @typedef {React.ComponentProps<"li">} SidebarMenuSubItemProps
 */

/**
 * SidebarMenuSubItem represents an individual sub-navigation item
 * inside a `SidebarMenuSub` list. Typically used for nested menu links or buttons.
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarMenuSubItemProps & React.RefAttributes<HTMLLIElement>
 * >}
 */
const SidebarMenuSubItem = React.forwardRef(function SidebarMenuSubItem(
  { ...props },
  ref
) {
  return <li ref={ref} {...props} />;
});
SidebarMenuSubItem.displayName = "SidebarMenuSubItem"

/**
 * @typedef {React.ComponentProps<"a"> & {
 *   asChild?: boolean,
 *   size?: "sm" | "md",
 *   isActive?: boolean
 * }} SidebarMenuSubButtonProps
 */

/**
 * SidebarMenuSubButton is a link or interactive element rendered inside
 * a `SidebarMenuSubItem`. It supports:
 * - `asChild` for custom rendering
 * - `size` for small/medium variants
 * - `isActive` for current page highlighting or selection state
 *
 * @type {React.ForwardRefExoticComponent<
 *   SidebarMenuSubButtonProps & React.RefAttributes<HTMLAnchorElement>
 * >}
 */
const SidebarMenuSubButton = React.forwardRef(function SidebarMenuSubButton(
  { asChild = false, size = "md", isActive, className, ...props },
  ref
) {
  const Comp = asChild ? Slot : "a"

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
})
SidebarMenuSubButton.displayName = "SidebarMenuSubButton"

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
}
